import { matchPath } from 'react-router-dom';
import { validatePermission } from '../../containers/authorization-helpers/validate-permission';
import { Permissions } from '../../models/enums/permissions';
import { Routes } from '../../utils/app-constants';

// What the sidebar needs to know about a section to tell whether the page is in it
export interface INavbarSection {
    title   : string;
    links   : Array<{ path: string; title?: string }>;
}

// Where a page is in the menu, for the trail in the top bar: Production > Planning
export interface IBreadcrumb {
    section?    : string;
    page        : string;
}

// What the sidebar needs to know about a section to tell whether the person sees it
export interface IVisibilityRules {
    requiresServiceCenter   : boolean;
    sectionPermissions      : Permissions[];
}

const COLLAPSED_KEY = 'navbar.collapsed';

// A sidebar with this many sections or fewer has them all open, as there is room for every link and nothing to be
// gained by closing one (Dashboard and Reports are not sections)
export const FIXED_OPEN_SECTION_LIMIT = 5;

// The section and the link that are the best match for the address, the same way a link finds out it is the active one
const bestMatch = (sections: INavbarSection[], pathname: string) => {

    let best : { section: INavbarSection; link: { path: string; title?: string } } | undefined;
    let matchLength = -1;

    sections.forEach((section : INavbarSection) => {
        section.links.forEach((link) => {

            if (link.path === Routes.UTILITY_NOT_READY) {
                return;
            }

            const match = matchPath(pathname, { path: link.path });
            if (match && match.url.length > matchLength) {
                best = { section, link };
                matchLength = match.url.length;
            }
        });
    });

    return best;
};

/**
 * The title of the section the page is in: the one with the link that is the best match for the address,
 * the same way a link finds out it is the active one.
 */
export const activeSectionTitle = (sections: INavbarSection[], pathname: string) : string | undefined => {
    const best = bestMatch(sections, pathname);
    return best ? best.section.title : undefined;
};

/**
 * Where the page is in the menu: its section and its link (Production, Planning), or only the name of Dashboard or
 * Reports, which are no part of a section.  Nothing for a page the menu has no link to, and no section when the link
 * has the name of its section (Routing, Routing), as it would say the same thing twice.
 */
export const breadcrumbOf = (sections: INavbarSection[], pathname: string) : IBreadcrumb | undefined => {

    const best = bestMatch(sections, pathname);
    if (best) {
        const page = best.link.title || best.section.title;
        return page === best.section.title ? { page } : { section: best.section.title, page };
    }

    if (matchPath(pathname, { path: Routes.DASHBOARD, exact: true })) {
        return { page: 'Dashboard' };
    }

    if (matchPath(pathname, { path: Routes.REPORTS_REPORTS })) {
        return { page: 'Reports' };
    }

    return undefined;
};

/**
 * How many sections the person sees: the ones they hold a permission for, and, for a section that is about a service
 * center, only when they have one.  This is the rule HasPermission and HasServiceCenters apply as the sections are drawn.
 */
export const visibleSectionCount = (
    sections        : IVisibilityRules[],
    permissions     : Permissions[] | undefined,
    serviceCenters  : string[] | undefined
) : number => {

    const hasServiceCenter = !!serviceCenters && serviceCenters.length > 0;

    return sections.filter((section : IVisibilityRules) =>
        validatePermission({
            currentPermissions  : permissions || [],
            requiredPermissions : section.sectionPermissions
        }) && (!section.requiresServiceCenter || hasServiceCenter)
    ).length;
};

/**
 * Whether the person left the sidebar collapsed to its icons, in this browser
 */
export const readCollapsed = () : boolean => {
    try {
        return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
    }
    catch (err) {
        // A browser that will not say is one that will not remember either
        return false;
    }
};

export const writeCollapsed = (collapsed : boolean) => {
    try {
        window.localStorage.setItem(COLLAPSED_KEY, collapsed ? 'true' : 'false');
    }
    catch (err) {
        // The sidebar still collapses, it is just not remembered
    }
};
